import { ReactNode, useState } from "react";

import { css, cssMap } from "@compiled/react";
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4Tabs,
    Adg4TabPanel
} from "@/components/adg4/tabs/src";
import Link, { LinkType } from "@/components/wpl/link/src";
import {
    DataEventProps,
    ProductKeyTypes,
    Row,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";

type BgColorType = "blue" | "black" | "white";

export interface PatternADG4TestimonialProps {
    bgColor?: BgColorType;
    heading?: string;
    subheading?: string;
    cta?: {
        text: string;
        url: string;
        dataEventProps?: DataEventProps;
    };
    testimonials: TestimonialsTypes[];
    productKey?: ProductKeyTypes;
    className?: string;
    imagePosition?: "left" | "right";
    testimonialCta?: ReactNode;
}

export type TestimonialsTypes = {
    eyebrow?: string;
    logo: JSX.Element;
    name: string;
    title: string;
    image: string;
    quote: string;
    link?: {
        text: string;
        url: string;
        type?: LinkType;
        dataEventProps?: DataEventProps;
    };
    label: string;
};

const AUTO_TRANSITION_INTERVAL = 8000;

const backgroundColorStyleMap = cssMap({
    blue: { background: "#1868DB" },
    black: { background: "#000" },
    white: { background: "#fff" }
});

const backgroundShapeStyleMap = cssMap({
    "image-left": {
        "clipPath": "polygon(2% 16%, 100% 0, 100% 100%, 0% 100%)",
        // hardcoded media query used since cssMap can't use media primitive
        "@media": {
            "not all and (min-width: 48rem)": {
                clipPath: "polygon(0 10%, 100% 0, 100% 100%, 0% 100%)"
            }
        }
    },
    "image-right": {
        "clipPath": "polygon(0 0, 100% 15%, 100% 100%, 0% 100%)",
        // hardcoded media query used since cssMap can't use media primitive
        "@media": {
            "not all and (min-width: 48rem)": {
                clipPath: "polygon(0 0, 100% 15%, 100% 100%, 0% 100%)"
            }
        }
    }
});

const customerImagesSectionStyleMap = cssMap({
    "image-left": {
        gridColumnStart: "unset"
    },
    "image-right": {
        "@media": {
            "(min-width: 64rem)": {
                gridColumnStart: "2"
            }
        }
    }
});

const customerQuotesSectionStyleMap = cssMap({
    "image-left": {
        gridColumnStart: "unset"
    },
    "image-right": {
        "@media": {
            "(min-width: 64rem)": {
                gridColumnStart: "1",
                gridRowStart: "1"
            }
        }
    }
});

const linkColorMap = {
    "dark-bg": "#fff",
    "white-bg": "#292A2E"
};

const fontColorStyleMap = cssMap({
    "dark-bg": { color: "#fff !important" },
    "white-bg": { color: "unset" }
});

const topLineStyleMap = cssMap({
    blue: { backgroundColor: "#add8e6" },
    black: { backgroundColor: "#fff" },
    white: { backgroundColor: "#000" }
});

const logoOptionStyleMap = cssMap({
    "dark-bg": {
        svg: {
            path: {
                fill: "#fff"
            }
        },
        selectors: {
            "&[aria-selected=true]": {
                ".top-marker": {
                    backgroundColor: "#fff"
                }
            }
        }
    },
    "white-bg": {
        svg: {
            path: {
                fill: "#000"
            }
        },
        selectors: {
            "&[aria-selected=true]": {
                ".top-marker": {
                    backgroundColor: "#000"
                }
            }
        }
    }
});

export const ADG4CustomerTestimonials = ({
    bgColor = "blue",
    heading,
    subheading,
    cta,
    testimonials,
    productKey,
    className,
    imagePosition = "left",
    testimonialCta
}: PatternADG4TestimonialProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    const logosContainerStyles = css({
        "display": testimonialCta ? "flex" : "block",
        ".logos": {
            "margin-left": testimonialCta ? "0" : "auto"
        }
    });

    const bgTheme =
        bgColor === "blue" || bgColor === "black" ? "dark-bg" : "white-bg";

    return (
        <Adg4Tabs tabSwitchInterval={AUTO_TRANSITION_INTERVAL} loop={true}>
            <TypographyStyles>
                <section
                    ref={scrollRef}
                    className={sectionIsVisible ? "is-visible" : ""}
                >
                    {!heading && !subheading && !cta ? null : (
                        <Row layout="8">
                            <div css={headerStyles}>
                                {heading && (
                                    <h2 css={h2Styles} className="">
                                        {heading}
                                    </h2>
                                )}
                                {subheading && (
                                    <p css={pStyles} className="body-lg">
                                        {subheading}
                                    </p>
                                )}
                                {cta && (
                                    <Link
                                        text={cta.text}
                                        href={cta.url}
                                        dataEventProps={cta.dataEventProps}
                                        type={"basic"}
                                        doesArrowAnimate={true}
                                    />
                                )}
                            </div>
                        </Row>
                    )}

                    <div
                        css={[
                            testimonialKeyframeStyles,
                            testimonialStyles,
                            fontColorStyleMap[bgTheme]
                        ]}
                        ref={scrollRef}
                        className={`${className} ${
                            sectionIsVisible ? "is-visible" : ""
                        } image-${imagePosition} ${bgColor}-background`}
                    >
                        <Row>
                            <div>
                                <div
                                    css={[
                                        bgShapeStyle,
                                        backgroundColorStyleMap[bgColor],
                                        backgroundShapeStyleMap[
                                            `image-${imagePosition}`
                                        ]
                                    ]}
                                    className="bgshape"
                                />
                                <div css={tabPanelWrapperStyles}>
                                    {testimonials.map((testimonial, index) => {
                                        return (
                                            <Adg4TabPanel
                                                id={`${index}`}
                                                panelIndex={index}
                                                key={index}
                                                forceRender={true}
                                            >
                                                <TopRowContent
                                                    testimonial={testimonial}
                                                    imagePosition={
                                                        imagePosition
                                                    }
                                                    index={index}
                                                    bgTheme={bgTheme}
                                                    bgColor={bgColor}
                                                />
                                            </Adg4TabPanel>
                                        );
                                    })}
                                </div>

                                <div css={rowWidthStyle} className="row-width">
                                    <div
                                        css={[
                                            logosContainerBaseStyle,
                                            logosContainerStyles
                                        ]}
                                        className="logos-container"
                                    >
                                        <Adg4DraggableTabList
                                            css={logosStyle}
                                            className="logos"
                                            rowGap="32px"
                                        >
                                            <div
                                                css={[
                                                    topLineStyles,
                                                    topLineStyleMap[bgColor]
                                                ]}
                                                className="top-line"
                                            />
                                            {testimonials.map(
                                                (testimonial, index) => {
                                                    return (
                                                        <Adg4DraggableTab
                                                            css={[
                                                                logoOptionStyleMap[
                                                                    bgTheme
                                                                ],
                                                                logoOptionStyle
                                                            ]}
                                                            className="logo-option"
                                                            key={index}
                                                            tabIndex={index}
                                                        >
                                                            <LogoTabContent
                                                                testimonialLogo={
                                                                    testimonial.logo
                                                                }
                                                                testimonialLabel={
                                                                    testimonial.label
                                                                }
                                                                productKey={
                                                                    productKey
                                                                }
                                                            />
                                                        </Adg4DraggableTab>
                                                    );
                                                }
                                            )}
                                        </Adg4DraggableTabList>
                                        {testimonialCta && testimonialCta}
                                    </div>
                                </div>
                            </div>
                        </Row>
                    </div>
                </section>
            </TypographyStyles>
        </Adg4Tabs>
    );
};

interface TopRowContentInterface {
    testimonial: TestimonialsTypes;
    imagePosition: "left" | "right";
    index: number;
    bgTheme: "dark-bg" | "white-bg";
    bgColor: BgColorType;
    isPanelActive?: boolean;
}

const TopRowContent = ({
    testimonial,
    imagePosition,
    index,
    bgTheme,
    bgColor,
    isPanelActive
}: TopRowContentInterface) => {
    return (
        <div
            css={[
                rowWidthStyle,
                topRowStyle,
                isPanelActive && topRowActiveStyle
            ]}
            className="row-width top-row"
        >
            <div
                css={[
                    customerImagesSectionStyles,
                    customerImagesSectionStyleMap[`image-${imagePosition}`]
                ]}
                className="customer-images-section"
            >
                <img
                    css={[
                        customerImagesSectionImageStyles,
                        isPanelActive && customerImagesImgActiveStyle
                    ]}
                    src={testimonial.image}
                    alt={testimonial.title}
                />
            </div>
            <div
                css={[
                    customerQuotesSectionStyle,
                    customerQuotesSectionStyleMap[`image-${imagePosition}`]
                ]}
                className="customer-quotes-section"
            >
                <div
                    css={testimonialQuotesSectionStyle}
                    className="testimonial-quotes-section"
                >
                    <div
                        css={[
                            quoteContainerStyle,
                            isPanelActive && quoteContainerActiveStyle
                        ]}
                        className="quote-container"
                        key={index}
                    >
                        {testimonial.eyebrow && (
                            <p
                                css={[
                                    fontColorStyleMap[bgTheme],
                                    quoteContainerPStyle
                                ]}
                                className="eyebrow-md"
                            >
                                {testimonial.eyebrow}
                            </p>
                        )}
                        <p
                            css={[
                                fontColorStyleMap[bgTheme],
                                quoteContainerPStyle
                            ]}
                            className="callout"
                        >
                            “{testimonial.quote}”
                        </p>
                        <p css={quoteContainerPStyle}>
                            <span
                                css={fontColorStyleMap[bgTheme]}
                                className="eyebrow-lg"
                            >
                                {testimonial.name}
                            </span>
                            <br />
                            <span
                                css={fontColorStyleMap[bgTheme]}
                                className="body-md title"
                            >
                                {testimonial.title}
                            </span>
                        </p>
                        {testimonial.link && (
                            <Link
                                customLinkColor={linkColorMap[bgTheme]}
                                text={testimonial.link.text}
                                href={testimonial.link.url}
                                type={testimonial.link.type ?? "basic"}
                                bgColor={
                                    bgColor === "blue" || bgColor === "black"
                                        ? "bgDark"
                                        : "bgWhite"
                                }
                                dataEventProps={testimonial.link.dataEventProps}
                                doesArrowAnimate={true}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const topRowActiveStyle = css({
    opacity: 1,
    zIndex: 2
});
const customerImagesImgActiveStyle = css({
    animation: "fadein 1s ease-out 1 forwards"
});

const quoteContainerActiveStyle = css({
    animation: "fadeswipe 1s 1",

    [media.below.sm]: {
        animation: "fadeswipesmall 1s 1"
    }
});

interface LogoTabContentProps {
    testimonialLabel: string;
    testimonialLogo: JSX.Element;
    productKey?: ProductKeyTypes;
    isSelected?: boolean;
}

const LogoTabContent = ({
    testimonialLabel,
    testimonialLogo,
    productKey,
    isSelected
}: LogoTabContentProps) => {
    const [isHover, setIsHover] = useState(false);

    return (
        <>
            <div css={isSelected && topMarkerStyle} className="top-marker" />
            <button
                aria-label={testimonialLabel}
                title={testimonialLabel}
                data-product-key={productKey}
                data-event-component="button"
                data-event-container="testimonialsSection"
                data-event="clicked"
                data-label-english={testimonialLabel}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                css={[logoButtonStyle]}
            >
                <div
                    css={[
                        logoImgStyle,
                        logoImgContainerStyle,
                        (isSelected || isHover) && logoImgHoverStyle
                    ]}
                    className="logo-img-container"
                >
                    {testimonialLogo}
                </div>
            </button>
        </>
    );
};

// LogoTabContent styles

const logoImgStyle = css({
    pointerEvents: "none",
    opacity: 0.5
});

const logoImgHoverStyle = css({
    transition: "0.5s",
    opacity: "1"
});

const logoButtonStyle = css({
    background: "none",
    border: 0,
    appearance: "none",
    paddingBlockStart: "20px",
    cursor: "pointer",
    position: "relative"
});

const topMarkerStyle = css({
    opacity: "1",
    position: "absolute",
    insetBlockStart: "-3px",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    height: "5px",
    borderRadius: "2px"
});

const logoImgContainerStyle = css({
    height: "34px",
    display: "flex",
    alignItems: "center"
});

// ADG4CustomerTestimonials styles

const headerStyles = css({
    textAlign: "center",
    marginBlockEnd: "40px"
});

const h2Styles = css({
    marginBlock: "40px 12px"
});

const pStyles = css({
    marginBlock: "0 12px"
});

const topLineStyles = css({
    position: "absolute",
    insetBlockStart: "1px",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    height: "1px",
    borderRadius: "2px",
    opacity: 0.5,

    [media.below.sm]: {
        insetBlockStart: "21px"
    }
});

const testimonialKeyframeStyles = css({
    "@keyframes fadeswipe": {
        from: {
            opacity: 0,
            transform: "translateX(100px)"
        },
        to: {
            opacity: 1,
            transform: "translateX(0px)"
        }
    },
    "@keyframes fadeswipesmall": {
        from: {
            opacity: 0,
            transform: "translateX(20px)"
        },
        to: {
            opacity: 1,
            transform: "translateX(0px)"
        }
    },

    "@keyframes fadein": {
        from: {
            opacity: 0
        },
        to: {
            opacity: 1
        }
    }
});

const rowWidthStyle = css({
    position: "relative",
    display: "flex",
    rowGap: token("space.250"),
    flexWrap: "wrap",
    alignItems: "stretch",

    [media.below.sm]: {
        flexDirection: "column",
        textAlign: "center"
    }
});

const topRowStyle = css({
    marginBlockEnd: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "32px",
    opacity: 0,
    zIndex: 1,

    [media.below.md]: {
        gridTemplateColumns: "1fr"
    }
});

const bgShapeStyle = css({
    position: "absolute",
    inset: 0,
    width: "95%",
    maxWidth: "1368px",
    margin: "auto",

    [media.below.sm]: {
        width: "100%"
    }
});

const customerImagesSectionStyles = css({
    position: "relative",
    flexBasis: "40%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",

    [media.only.sm]: {
        flexBasis: "50%"
    },

    [media.below.sm]: {
        width: "90%",
        margin: "0 auto"
    },
    [media.only.xs]: {
        width: "70%",
        margin: "0 auto"
    }
});

const customerImagesSectionImageStyles = css({
    height: "365px",

    [media.only.sm]: {
        height: "258px"
    },

    [media.only.xs]: {
        height: "258px"
    },

    [media.only.xxs]: {
        height: "210px"
    },

    width: "auto",
    display: "block"
});

const customerQuotesSectionStyle = css({
    flexBasis: "60%",
    overflow: "hidden",

    [media.below.md]: {
        flexBasis: "50%"
    }
});

const testimonialQuotesSectionStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "end",
    borderRadius: "20px",
    position: "relative",
    margin: "0 auto",

    [media.above.md]: {
        paddingBlockStart: "80px"
    },

    [media.only.sm]: {
        width: "66%"
    },

    [media.below.sm]: {
        width: "100%"
    }
});

const quoteContainerStyle = css({
    transition: "opacity 1s",
    display: "flex",
    flexDirection: "column",
    gap: "24px"
});

const quoteContainerPStyle = css({
    marginBlock: "0"
});

const logosContainerBaseStyle = css({
    "overflow": "hidden",
    "width": "100%",
    "justifyContent": "space-between",

    "@media (max-width: 1140px)": {
        flexDirection: "column"
    },

    [media.below.sm]: {
        marginBlockStart: "20px"
    }
});

const logosStyle = css({
    // !important used to override Adg4DraggableTabList styles
    marginBlockEnd: "40px !important",
    paddingBlockStart: "1px !important",
    listStyle: "none !important"
});

const logoOptionStyle = css({
    "display": "flex",
    "justifyContent": "stretch",
    "alignItems": "stretch",
    "position": "relative",

    "&[aria-selected=true]": {
        paddingBlockStart: "0"
    }
});

const testimonialStyles = css({
    position: "relative",
    zIndex: 1,
    marginBlockEnd: token("space.500")
});

const tabPanelWrapperStyles = css({
    "display": "grid",
    "gridTemplateColumns": "1fr",
    "gridTemplateRows": "1fr",

    // active panel style overrides nested here in order to expand
    // the container to the height of largest child.
    ".tab-panel": {
        "opacity": 0,
        "zIndex": 1,
        "display": "flex",
        "gridArea": "1 / 1",

        "&.active-panel": {
            opacity: 1,
            zIndex: 2
        }
    }
});
